import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon.tsx";
import Card from "components/Card/Card";
import {
  ControlledCheckbox,
  ControlledInput,
} from "components/Card/ControlledInput";
import { KaboodleToaster } from "components/common";
import { MaskedInput, TextInput } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import { Tooltip } from "components/Tooltip/Tooltip";
import { omit } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  prescriptionAllocationAPI,
  usePatchPrescriptionAllocationByIdMutation,
} from "services/msa-api";

import { getServiceField } from "../../../future-medical-utils";
import { useAllocationDragDrop } from "../../hooks/useAllocationDragDrop";
import { useAllocationUpdate } from "../../hooks/useAllocationUpdate";
import {
  ALLOCATION_DND_TYPES,
  OMIT_FIELDS,
  TIMING,
} from "../../utils/allocation-constants";
import { sanitizePrescriptionAllocationData } from "../../utils/allocation-sanitization";
import { validatePrescriptionField } from "../../utils/allocation-validation";
import { DropIndicator } from "../DropIndicator/DropIndicator";
import { TotalWithProbability } from "../TotalWithProbability";

import type { AppDispatch } from "services/store";
import type {
  AllocationRowWithSection,
  PrescriptionAllocationRow as PrescriptionAllocationRowType,
  PrescriptionAllocationRowProps,
} from "../../utils/allocation-types";

type Props = PrescriptionAllocationRowProps & {
  onDrop: (
    draggedRow: PrescriptionAllocationRowProps,
    dropTargetRow: AllocationRowWithSection<PrescriptionAllocationRowProps>,
  ) => void;
  totalRowsInSection: number;
};

/**
 * editable row for prescription allocations
 * integrates with Redbook drug database, supports exposure/mitigation, and handles complex validation
 * optimistically updates checkboxes while debouncing text input changes
 */
export const PrescriptionAllocationRow = (props: Props) => {
  const { onDrop, totalRowsInSection, ...row } = props;
  const dispatch = useDispatch<AppDispatch>();
  const [patchPrescriptionAllocationById] =
    usePatchPrescriptionAllocationByIdMutation();
  const { executeUpdate, isAnyRowUpdating } = useAllocationUpdate({
    rowId: row.id,
  });
  const [showOverrideConfirm, setShowOverrideConfirm] = useState(false);

  const {
    reset,
    control,
    formState: { errors },
    getValues,
    setValue,
  } = useForm({
    defaultValues: row,
  });

  const isRedbookField = useMemo(() => {
    // fields that come from redbook and should be readonly unless overridden
    return new Set([
      "name",
      "active_ingredient",
      "formulation",
      "ndc",
      "dosage",
      "is_generic",
      "unit_price",
    ]);
  }, []);

  const handleFieldSave = useCallback(
    async (
      fieldName: string,
      value: string | number | boolean,
      immediate = false,
    ) => {
      // validate the field before saving
      const validationError = validatePrescriptionField(fieldName, value);
      if (validationError) {
        KaboodleToaster.error(validationError);
        return;
      }

      executeUpdate(
        async () => {
          try {
            const updatedData = {
              ...getValues(),
              [fieldName]: value,
            };
            const cleanedData = omit(updatedData, OMIT_FIELDS);
            const sanitizedData =
              sanitizePrescriptionAllocationData(cleanedData);

            await patchPrescriptionAllocationById({
              id: row.id,
              data: sanitizedData,
            }).unwrap();
          } catch (error) {
            console.error("PrescriptionAllocationRow", error);
            KaboodleToaster.error("Failed to update prescription allocation");
            throw error;
          }
        },
        {
          rowId: row.id,
          immediate,
          debounceMs: immediate ? TIMING.IMMEDIATE : TIMING.DEBOUNCE_MS,
          maxWaitMs: immediate ? TIMING.IMMEDIATE : TIMING.MAX_WAIT_MS,
        },
      );
    },
    [row.id, getValues, patchPrescriptionAllocationById, executeUpdate],
  );

  const isFieldReadOnly = useCallback(
    (fieldName: string) => {
      if (!row.redbook_drug_product) return false;
      if (row.overridden) return false;
      return isRedbookField.has(fieldName);
    },
    [row.redbook_drug_product, row.overridden, isRedbookField],
  );

  const handleOverride = useCallback(async () => {
    try {
      await handleFieldSave("overridden", true);
      KaboodleToaster.success(
        "Redbook values can now be edited. Note: This cannot be undone.",
      );
      setShowOverrideConfirm(false);
    } catch (error) {
      KaboodleToaster.error("Failed to enable edit mode");
    }
  }, [handleFieldSave]);

  const handleExposureMitigationChange = useCallback(
    (checked: boolean, field: "has_exposure" | "has_mitigation") => {
      // prepare the update data with default values when checking exposure/mitigation
      const updateData: Record<string, string | number | boolean> = {
        [field]: checked,
      };

      // when checking exposure for the first time, default the exposure fields to parent values
      if (field === "has_exposure" && checked && !row.has_exposure) {
        updateData.exposed_unit_price = row.unit_price ?? 0;
        updateData.exposed_units_per_month = row.units_per_month ?? 0;
        updateData.exposed_units_per_year = row.units_per_year ?? 12;
        updateData.exposed_dosage = row.dosage ?? "";
        updateData.number_of_years_exposed = row.number_of_years ?? 0;

        // immediately update form state for instant visual feedback
        setValue("exposed_unit_price", row.unit_price ?? 0);
        setValue("exposed_units_per_month", row.units_per_month ?? 0);
        setValue("exposed_units_per_year", row.units_per_year ?? 12);
        setValue("exposed_dosage", row.dosage ?? "");
        setValue("number_of_years_exposed", row.number_of_years ?? 0);
      }

      // when checking mitigation for the first time, default the mitigation fields to parent values
      if (field === "has_mitigation" && checked && !row.has_mitigation) {
        updateData.mitigated_unit_price = row.unit_price ?? 0;
        updateData.mitigated_units_per_month = row.units_per_month ?? 0;
        updateData.mitigated_units_per_year = row.units_per_year ?? 12;
        updateData.mitigated_dosage = row.dosage ?? "";
        updateData.number_of_years_mitigated = row.number_of_years ?? 0;

        // immediately update form state for instant visual feedback
        setValue("mitigated_unit_price", row.unit_price ?? 0);
        setValue("mitigated_units_per_month", row.units_per_month ?? 0);
        setValue("mitigated_units_per_year", row.units_per_year ?? 12);
        setValue("mitigated_dosage", row.dosage ?? "");
        setValue("number_of_years_mitigated", row.number_of_years ?? 0);
      }

      // optimistic update
      const patchResult = dispatch(
        prescriptionAllocationAPI.util.updateQueryData(
          "getPrescriptionAllocation",
          { filters: getServiceField(row.service, true) },
          (draft) => {
            if (!draft?.results) return;
            const rowToUpdate = draft.results.find(
              (r: PrescriptionAllocationRowType) => r.id === row.id,
            );
            if (rowToUpdate) {
              Object.assign(rowToUpdate, updateData);
            }
          },
        ),
      );

      executeUpdate(
        async () => {
          try {
            const updatedData = {
              ...getValues(),
              ...updateData,
            };

            await patchPrescriptionAllocationById({
              id: row.id,
              data: omit(updatedData, OMIT_FIELDS),
            }).unwrap();
          } catch (error) {
            patchResult.undo();
            console.error("PrescriptionAllocationRow", error);
            KaboodleToaster.error("Failed to update prescription allocation");
            throw error;
          }
        },
        {
          rowId: row.id,
          immediate: true,
          debounceMs: 0,
          maxWaitMs: 0,
        },
      );
    },
    [
      row,
      getValues,
      setValue,
      dispatch,
      patchPrescriptionAllocationById,

      executeUpdate,
    ],
  );

  useEffect(() => {
    reset(row, {
      keepDirtyValues: true,
    });
  }, [reset, row.id, row.modified]);

  // use centralized drag-drop hook with dev branch logic
  const { drag, drop, isDragging, showDropIndicator, showIndicatorBelow } =
    useAllocationDragDrop({
      row: { ...row, ...getValues() },
      totalRowsInSection,
      dndType: ALLOCATION_DND_TYPES.PRESCRIPTION,
      onDrop,
    });

  return (
    <>
      {showOverrideConfirm && (
        <KaboodleModal
          isOpen={showOverrideConfirm}
          onClose={() => setShowOverrideConfirm(false)}
          footer={
            <div className="flex jc-c">
              <Button className="delete" onClick={handleOverride}>
                Override
              </Button>
              <Button onClick={() => setShowOverrideConfirm(false)}>
                Cancel
              </Button>
            </div>
          }
        >
          <Card heading="Enable Edit Mode">
            <p>
              Are you sure you want to enable edit mode for this Redbook
              prescription? This action cannot be undone and will allow
              modification of Redbook values.
            </p>
          </Card>
        </KaboodleModal>
      )}

      <div ref={drop} style={{ display: "contents" }}>
        <figure
          ref={drag}
          className="drag-handle"
          style={{
            cursor: "grab",
            opacity: isDragging ? 0.5 : 1,
            position: "relative",
          }}
          aria-label="Drag to reorder"
        >
          {showDropIndicator && (
            <DropIndicator showBelow={showIndicatorBelow} />
          )}
          ⋮⋮
        </figure>

        <ControlledCheckbox
          control={control}
          name="has_exposure"
          label=""
          errors={errors}
          readOnly={isAnyRowUpdating}
          onSave={async (value) => {
            handleExposureMitigationChange(value, "has_exposure");
          }}
        />
        <ControlledCheckbox
          control={control}
          name="has_mitigation"
          label=""
          errors={errors}
          readOnly={isAnyRowUpdating}
          onSave={async (value) => {
            handleExposureMitigationChange(value, "has_mitigation");
          }}
        />
        {row.is_excluded && (
          <ControlledCheckbox
            control={control}
            name="non_medicare"
            label=""
            errors={errors}
            readOnly={isAnyRowUpdating}
            onSave={(value) => handleFieldSave("non_medicare", value, true)}
          />
        )}

        <ControlledCheckbox
          control={control}
          name="is_generic"
          label=""
          errors={errors}
          onSave={(value) => handleFieldSave("is_generic", value, true)}
          readOnly={isFieldReadOnly("is_generic") || isAnyRowUpdating}
          disabledReason="Override this row to edit this field"
        />

        <ControlledInput
          control={control}
          name="name"
          errors={errors}
          onSave={(value) => handleFieldSave("name", value)}
          componentType={TextInput}
          maxLength={40}
          readOnly={isFieldReadOnly("name")}
        />

        <ControlledInput
          control={control}
          name="active_ingredient"
          errors={errors}
          onSave={(value) => handleFieldSave("active_ingredient", value)}
          componentType={TextInput}
          maxLength={40}
          readOnly={isFieldReadOnly("active_ingredient")}
        />

        <ControlledInput
          control={control}
          name="formulation"
          errors={errors}
          onSave={(value) => handleFieldSave("formulation", value)}
          componentType={TextInput}
          maxLength={20}
          readOnly={isFieldReadOnly("formulation")}
        />

        <ControlledInput
          control={control}
          name="ndc"
          errors={errors}
          onSave={(value) => handleFieldSave("ndc", value)}
          componentType={TextInput}
          maxLength={15}
          readOnly={isFieldReadOnly("ndc")}
        />

        <ControlledInput
          control={control}
          name="dosage"
          errors={errors}
          onSave={(value) => handleFieldSave("dosage", value)}
          componentType={TextInput}
          maxLength={40}
          readOnly={isFieldReadOnly("dosage")}
        />

        <ControlledInput
          control={control}
          name="units_per_month"
          mask="number"
          errors={errors}
          onSave={(value) => handleFieldSave("units_per_month", value)}
          componentType={MaskedInput}
          maxLength={4}
        />

        <ControlledInput
          control={control}
          name="unit_price"
          mask="dollar-four-dec"
          errors={errors}
          readOnly={isFieldReadOnly("unit_price")}
          onSave={(value) => handleFieldSave("unit_price", value)}
          componentType={MaskedInput}
        />

        <ControlledInput
          control={control}
          name="units_per_year"
          mask="number"
          errors={errors}
          onSave={(value) => handleFieldSave("units_per_year", value)}
          componentType={MaskedInput}
          maxLength={2}
        />

        <ControlledInput
          control={control}
          name="number_of_years"
          mask="number"
          errors={errors}
          onSave={(value) => handleFieldSave("number_of_years", value)}
          componentType={MaskedInput}
          maxLength={2}
        />

        {row.is_excluded ? (
          <TotalWithProbability<PrescriptionAllocationRowProps>
            control={control}
            errors={errors}
            handleFieldSave={handleFieldSave}
            probability={row.probability_of_success}
          />
        ) : (
          <ControlledInput
            control={control}
            name="total_cost"
            mask="total_dollar"
            errors={errors}
            readOnly={true}
            onSave={(value) => handleFieldSave("total_cost", value)}
            componentType={MaskedInput}
          />
        )}

        <figure className="data-values-btn flex">
          <Tooltip content="Clone row">
            <ButtonIcon
              size="small"
              className="data-value page-view flex as-c"
              onClick={() => row?.onCloneRow?.(row)}
            >
              content_copy
            </ButtonIcon>
          </Tooltip>
        </figure>
        <figure className="data-values-btn flex">
          <Tooltip content="Delete row">
            <ButtonIcon
              size="small"
              className="data-value delete flex as-c"
              onClick={() => row?.onSetRowToDelete(row)}
            >
              delete
            </ButtonIcon>
          </Tooltip>
        </figure>

        <figure className="data-values-btn flex">
          {row.redbook_drug_product && !row.overridden && (
            <Tooltip content="Override Redbook values">
              <ButtonIcon
                size="small"
                className="data-value page-view flex as-c"
                onClick={() => setShowOverrideConfirm(true)}
              >
                edit
              </ButtonIcon>
            </Tooltip>
          )}
        </figure>

        <div
          className={`exposure-mitigation-wrapper w-prescription ${row.is_excluded ? "excluded" : "included-row"}`}
        >
          {row.has_exposure && !row.is_excluded && (
            <div className="exposure-mitigation-wrapper-sub-grid">
              <figure className="exposure-adjustment">
                Exposure Adjustments
                <span className="material-icons">arrow_right_alt</span>
              </figure>

              <ControlledInput
                control={control}
                name="exposed_dosage"
                errors={errors}
                onSave={(value) => handleFieldSave("exposed_dosage", value)}
                componentType={TextInput}
                maxLength={40}
              />

              <ControlledInput
                control={control}
                name="exposed_units_per_month"
                errors={errors}
                onSave={(value) =>
                  handleFieldSave("exposed_units_per_month", value)
                }
                componentType={MaskedInput}
                mask="number"
                maxLength={4}
              />
              <ControlledInput
                control={control}
                name="exposed_unit_price"
                mask="dollar-four-dec"
                errors={errors}
                onSave={(value) => handleFieldSave("exposed_unit_price", value)}
                componentType={MaskedInput}
              />
              <ControlledInput
                control={control}
                name="exposed_units_per_year"
                mask="number"
                errors={errors}
                onSave={(value) =>
                  handleFieldSave("exposed_units_per_year", value)
                }
                componentType={MaskedInput}
                maxLength={2}
              />
              <ControlledInput
                control={control}
                name="number_of_years_exposed"
                mask="number"
                errors={errors}
                onSave={(value) =>
                  handleFieldSave("number_of_years_exposed", value)
                }
                componentType={MaskedInput}
                maxLength={2}
              />
              <ControlledInput
                control={control}
                name="total_exposure"
                mask="total_dollar"
                errors={errors}
                readOnly={true}
                onSave={(value) => handleFieldSave("total_exposure", value)}
                componentType={MaskedInput}
              />

              <figure></figure>
            </div>
          )}
          {row.has_mitigation && (
            <div className="exposure-mitigation-wrapper-sub-grid">
              <figure className="exposure-adjustment">
                Mitigation Adjustments
                <span className="material-icons">arrow_right_alt</span>
              </figure>

              <ControlledInput
                control={control}
                name="mitigated_dosage"
                errors={errors}
                onSave={(value) => handleFieldSave("mitigated_dosage", value)}
                componentType={TextInput}
                maxLength={40}
              />

              <ControlledInput
                control={control}
                name="mitigated_units_per_month"
                errors={errors}
                onSave={(value) =>
                  handleFieldSave("mitigated_units_per_month", value)
                }
                componentType={MaskedInput}
                mask="number"
                maxLength={5}
              />
              <ControlledInput
                control={control}
                name="mitigated_unit_price"
                mask="dollar-four-dec"
                errors={errors}
                onSave={(value) =>
                  handleFieldSave("mitigated_unit_price", value)
                }
                componentType={MaskedInput}
              />
              <ControlledInput
                control={control}
                name="mitigated_units_per_year"
                mask="number"
                errors={errors}
                onSave={(value) =>
                  handleFieldSave("mitigated_units_per_year", value)
                }
                componentType={MaskedInput}
                maxLength={2}
              />
              <ControlledInput
                control={control}
                name="number_of_years_mitigated"
                mask="number"
                errors={errors}
                onSave={(value) =>
                  handleFieldSave("number_of_years_mitigated", value)
                }
                componentType={MaskedInput}
                maxLength={2}
              />
              <ControlledInput
                control={control}
                name="total_mitigation"
                mask="total_dollar"
                errors={errors}
                readOnly={true}
                onSave={(value) => handleFieldSave("total_mitigation", value)}
                componentType={MaskedInput}
              />
              <figure></figure>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
